<template>
  <div v-if="totalItems > 0" class="flex flex-col items-center space-y-4 mt-12">
    <!-- Page Info -->
    <div class="text-sm text-gray-600">
      Showing {{ startItem }} to {{ endItem }} of {{ totalItems }} results
    </div>

    <!-- Pagination Controls -->
    <nav
      class="flex items-center space-x-2"
      role="navigation"
      aria-label="Pagination"
    >
      <!-- Previous Button -->
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="currentPage === 1"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        :class="{
          'hover:text-slate-900 hover:border-slate-300': currentPage > 1,
        }"
        aria-label="Go to previous page"
      >
        <svg
          class="w-4 h-4 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Previous
      </button>

      <!-- Page Numbers -->
      <div class="flex items-center space-x-1">
        <template v-for="page in visiblePages" :key="page">
          <!-- Page Number Button -->
          <button
            v-if="page !== '...'"
            @click="goToPage(page)"
            :class="[
              'px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
              page === currentPage
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-gray-600 bg-white border border-gray-200 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300',
            ]"
            :aria-label="`Go to page ${page}`"
            :aria-current="page === currentPage ? 'page' : undefined"
          >
            {{ page }}
          </button>

          <!-- Ellipsis -->
          <span
            v-else
            class="px-3 py-2 text-sm text-gray-500"
            aria-hidden="true"
          >
            ...
          </span>
        </template>
      </div>

      <!-- Next Button -->
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        :class="{
          'hover:text-slate-900 hover:border-slate-300': currentPage < totalPages,
        }"
        aria-label="Go to next page"
      >
        Next
        <svg
          class="w-4 h-4 ml-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </nav>
  </div>
</template>

<script setup>
const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  showPageSizeSelector: {
    type: Boolean,
    default: false,
  },
  maxVisiblePages: {
    type: Number,
    default: 5,
  },
});

const emit = defineEmits(["update:currentPage"]);

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.itemsPerPage;
  return end > props.totalItems ? props.totalItems : end;
});

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = props.currentPage;
  const maxVisible = props.maxVisiblePages;

  if (total <= maxVisible) {

    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {

    pages.push(1);

    let start = Math.max(2, current - Math.floor((maxVisible - 3) / 2));
    let end = Math.min(total - 1, start + (maxVisible - 3));

    if (end === total - 1) {
      start = Math.max(2, end - (maxVisible - 3));
    }

    if (start > 2) {
      pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total - 1) {
      pages.push("...");
    }

    if (total > 1) {
      pages.push(total);
    }
  }

  return pages;
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit("update:currentPage", page);
  }
};

const handleKeydown = (event) => {
  switch (event.key) {
    case "ArrowLeft":
      if (props.currentPage > 1) {
        event.preventDefault();
        goToPage(props.currentPage - 1);
      }
      break;
    case "ArrowRight":
      if (props.currentPage < totalPages.value) {
        event.preventDefault();
        goToPage(props.currentPage + 1);
      }
      break;
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
/* Custom focus styles for better accessibility */
button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

select:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Smooth transitions for hover effects */
button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active state for page buttons */
button:active {
  transform: scale(0.95);
}

/* Better disabled state */
button:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  nav {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .px-4 {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .px-3 {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>
